import { atom, getDefaultStore, useAtom, useSetAtom } from 'jotai'
import { atomWithRefresh, atomWithStorage } from 'jotai/utils'
import { useEffect } from 'react'
import { useDynamicConstants } from 'src/services/constants'
import {
  getPreferenceFromLocalStorage,
  PROMPT_STYLE,
  PROMPT_STYLE_VALUE,
  TSUBAKI_PROMPT_HELPER,
  TSUBAKI_PROMPT_HELPER_VALUE,
  TSUBAKI2_PROMPT_HELPER,
  TSUBAKI2_PROMPT_HELPER_VALUE,
} from 'src/services/helpers/preference'
import { attemptParseJSON } from 'src/utils'
import { getLocalStorage } from '~/utils/storage'

const PROMPT_HELPER_STORAGE_KEY = 'generator::prompts-helper'

const PROMPT_HELPER_TSUBAKI_STORAGE_KEY = 'generator::prompts-helper-tsubaki'

const PROMPT_HELPER_TSUBAKI2_STORAGE_KEY = 'generator::prompts-helper-tsubaki2'

const FALLBACK_IN_HOUSE_DIT_MODEL_IDS = [
  '1884107375027888751', // # Tsubaki v1.0
  '1911477338694141481', // # Serin
  '1934789863877919615', // # Tsubaki-Flash
  '1935090614966005945', // # Tsubaki v1.1
]

// Keep dynamic list in jotai, default to fallback
const inHouseDitModelIdsAtom = atom<string[]>(FALLBACK_IN_HOUSE_DIT_MODEL_IDS)

/**
 * Initialize in-house DiT model IDs from dynamic config.
 */
export function useInitInHouseDitModelIds() {
  const { data } = useDynamicConstants()
  const setIds = useSetAtom(inHouseDitModelIdsAtom, { store: defaultStore })

  useEffect(() => {
    const remoteIds = data?.inHouseDitModelIds
    if (Array.isArray(remoteIds) && remoteIds.length > 0) {
      // De-dup and filter out invalid entries
      const normalized = Array.from(new Set(remoteIds.filter(Boolean)))
      setIds(normalized)
    }
  }, [data?.inHouseDitModelIds])
}

export function isInHouseDitModel(modelId?: string): boolean {
  if (!modelId) {
    return false
  }
  const ids = defaultStore.get(inHouseDitModelIdsAtom)
  const list =
    Array.isArray(ids) && ids.length > 0 ? ids : FALLBACK_IN_HOUSE_DIT_MODEL_IDS
  return list.includes(modelId)
}

const FALLBACK_IN_HOUSE_MMDIT_MODEL_IDS = [
  '1982880136609467518', // # Tsubaki 2
]

const inHouseMmditModelIdsAtom = atom<string[]>(
  FALLBACK_IN_HOUSE_MMDIT_MODEL_IDS,
)

/**
 * Initialize in-house MMDIT26A model IDs from dynamic config.
 */
export function useInitInHouseMmditModelIds() {
  const { data } = useDynamicConstants()
  const setIds = useSetAtom(inHouseMmditModelIdsAtom, { store: defaultStore })

  useEffect(() => {
    const remoteIds = data?.inHouseMMDIT26AModelIds
    if (Array.isArray(remoteIds) && remoteIds.length > 0) {
      const normalized = Array.from(new Set(remoteIds.filter(Boolean)))
      setIds(normalized)
    }
  }, [data?.inHouseMMDIT26AModelIds])
}

export function isInHouseMmditModel(modelId?: string): boolean {
  if (!modelId) {
    return false
  }
  const ids = defaultStore.get(inHouseMmditModelIdsAtom)
  const list =
    Array.isArray(ids) && ids.length > 0
      ? ids
      : FALLBACK_IN_HOUSE_MMDIT_MODEL_IDS
  return list.includes(modelId)
}

const helperModeAtom = atomWithStorage<boolean | undefined>(
  PROMPT_HELPER_STORAGE_KEY,
  getHelperModeDefaultValue(PROMPT_HELPER_STORAGE_KEY),
)

/**
 * Added after tsubaki model released.
 * Due to the different requirement of prompt between Tsubaki model and the others,
 * It is prefer to seperate user config between tsubaki model and the others.
 * So, add a seperate atom to store it, and get the final state uniformly by `promptHelperModeAtom`
 */
const tsubakiHelperModeAtom = atomWithStorage<boolean | undefined>(
  PROMPT_HELPER_TSUBAKI_STORAGE_KEY,
  getHelperModeDefaultValue(PROMPT_HELPER_TSUBAKI_STORAGE_KEY),
)

const tsubaki2HelperModeAtom = atomWithStorage<boolean | undefined>(
  PROMPT_HELPER_TSUBAKI2_STORAGE_KEY,
  getHelperModeDefaultValue(PROMPT_HELPER_TSUBAKI2_STORAGE_KEY),
)

const selectedModelIdAtom = atom<string | undefined>(undefined)

const promptHelperModeAtom = atomWithRefresh(
  get => {
    const modelId = get(selectedModelIdAtom)
    if (isInHouseMmditModel(modelId)) {
      const tsubaki2PromptHelperPref = getPreferenceFromLocalStorage(
        TSUBAKI2_PROMPT_HELPER,
      )
      if (
        tsubaki2PromptHelperPref === TSUBAKI2_PROMPT_HELPER_VALUE.autoEnable
      ) {
        return true
      } else {
        return get(tsubaki2HelperModeAtom) ?? true
      }
    } else if (isInHouseDitModel(modelId)) {
      const tsubakiPromptHelperPref = getPreferenceFromLocalStorage(
        TSUBAKI_PROMPT_HELPER,
      )
      if (tsubakiPromptHelperPref === TSUBAKI_PROMPT_HELPER_VALUE.autoEnable) {
        return true
      } else {
        return get(tsubakiHelperModeAtom) ?? true
      }
    } else {
      const promptSelperPref = getPreferenceFromLocalStorage(PROMPT_STYLE)
      return (
        get(helperModeAtom) ??
        promptSelperPref !== PROMPT_STYLE_VALUE.preciseTags
      )
    }
  },
  (get, set, value: boolean) => {
    const modelId = get(selectedModelIdAtom)
    if (isInHouseMmditModel(modelId)) {
      set(tsubaki2HelperModeAtom, value)
    } else if (isInHouseDitModel(modelId)) {
      set(tsubakiHelperModeAtom, value)
    } else {
      set(helperModeAtom, value)
    }
  },
)

export function usePromptHelperMode() {
  return useAtom(promptHelperModeAtom, { store: defaultStore })
}

export function useSyncCurrentSelectedModelId() {
  return useSetAtom(selectedModelIdAtom, { store: defaultStore })
}

const defaultStore = getDefaultStore()

/**
 * Get default mode value from user's localstorage.
 * If user set prompt helper mode in the past, we can resume the user config.
 */
function getHelperModeDefaultValue(key: string): boolean | undefined {
  const storageValue = getLocalStorage(key)
  if (!storageValue) {
    return undefined
  }

  return attemptParseJSON(storageValue)
}
